/**
 * Utilitário para verificar se uma loja está aberta baseado no horário de funcionamento
 */

/**
 * Enum dos dias da semana
 */
export const DIAS_SEMANA = {
  DOMINGO: 0,
  SEGUNDA: 1,
  TERCA: 2,
  QUARTA: 3,
  QUINTA: 4,
  SEXTA: 5,
  SABADO: 6
};

/**
 * Nomes dos dias em português
 */
export const NOMES_DIAS = {
  [DIAS_SEMANA.DOMINGO]: 'domingo',
  [DIAS_SEMANA.SEGUNDA]: 'segunda',
  [DIAS_SEMANA.TERCA]: 'terça',
  [DIAS_SEMANA.QUARTA]: 'quarta',
  [DIAS_SEMANA.QUINTA]: 'quinta',
  [DIAS_SEMANA.SEXTA]: 'sexta',
  [DIAS_SEMANA.SABADO]: 'sábado'
};

/**
 * Mapeamento de strings para enum de dias
 */
const STRING_PARA_DIA = {
  'domingo': DIAS_SEMANA.DOMINGO,
  'dom': DIAS_SEMANA.DOMINGO,
  'segunda': DIAS_SEMANA.SEGUNDA,
  'segunda-feira': DIAS_SEMANA.SEGUNDA,
  'seg': DIAS_SEMANA.SEGUNDA,
  'terça': DIAS_SEMANA.TERCA,
  'terça-feira': DIAS_SEMANA.TERCA,
  'terca': DIAS_SEMANA.TERCA,
  'terca-feira': DIAS_SEMANA.TERCA,
  'ter': DIAS_SEMANA.TERCA,
  'quarta': DIAS_SEMANA.QUARTA,
  'quarta-feira': DIAS_SEMANA.QUARTA,
  'qua': DIAS_SEMANA.QUARTA,
  'quinta': DIAS_SEMANA.QUINTA,
  'quinta-feira': DIAS_SEMANA.QUINTA,
  'qui': DIAS_SEMANA.QUINTA,
  'sexta': DIAS_SEMANA.SEXTA,
  'sexta-feira': DIAS_SEMANA.SEXTA,
  'sex': DIAS_SEMANA.SEXTA,
  'sábado': DIAS_SEMANA.SABADO,
  'sabado': DIAS_SEMANA.SABADO,
  'sab': DIAS_SEMANA.SABADO
};

/**
 * Converte hora no formato "14h30" ou "14h" para minutos desde meia-noite
 * @param {string} horaStr - Hora no formato "14h30" ou "14h"
 * @returns {number|null} - Minutos desde meia-noite ou null se inválido
 */
function parseHora(horaStr) {
  if (!horaStr || typeof horaStr !== 'string') return null;
  
  // Remove espaços e converte para minúsculas
  horaStr = horaStr.trim().toLowerCase().replace(/\s+/g, '');
  
  // Regex: captura horas e minutos opcionais
  const match = horaStr.match(/(\d{1,2})h(\d{2})?/);
  if (!match) return null;
  
  const horas = parseInt(match[1], 10);
  const minutos = match[2] ? parseInt(match[2], 10) : 0;
  
  // Validação
  if (horas < 0 || horas > 23 || minutos < 0 || minutos > 59) {
    return null;
  }
  
  return horas * 60 + minutos;
}

/**
 * Normaliza o nome do dia removendo acentos
 */
function normalizarDia(diaStr) {
  if (!diaStr || typeof diaStr !== 'string') return '';
  return diaStr.toLowerCase().trim()
    .replace(/[àáâã]/g, 'a')
    .replace(/[èéê]/g, 'e')
    .replace(/[ìíî]/g, 'i')
    .replace(/[òóôõ]/g, 'o')
    .replace(/[ùúû]/g, 'u')
    .replace(/ç/g, 'c');
}

/**
 * Converte string de dia para enum
 */
function stringParaDia(diaStr) {
  const normalizado = normalizarDia(diaStr);
  return STRING_PARA_DIA[normalizado];
}

/**
 * Valida e normaliza a estrutura de horário de funcionamento
 * Aceita tanto a nova estrutura (objeto) quanto a antiga (string) para compatibilidade
 * 
 * @param {object|string} horarioFuncionamento - Estrutura de horário
 * @returns {object|null} - Estrutura normalizada ou null se inválido
 */
function normalizarHorarioFuncionamento(horarioFuncionamento) {
  if (!horarioFuncionamento) return null;
  
  // Se já é um objeto, valida e retorna
  if (typeof horarioFuncionamento === 'object' && !Array.isArray(horarioFuncionamento)) {
    const horarioNormalizado = {};
    
    // Itera sobre cada dia
    for (const [diaStr, horario] of Object.entries(horarioFuncionamento)) {
      const diaEnum = stringParaDia(diaStr);
      
      if (diaEnum === undefined) {
        console.warn(`Dia inválido ignorado: ${diaStr}`);
        continue;
      }
      
      // Se é null ou false, o dia está fechado
      if (horario === null || horario === false) {
        horarioNormalizado[diaEnum] = null;
        continue;
      }
      
      // Se é um objeto com inicio e fim
      if (typeof horario === 'object' && horario.inicio && horario.fim) {
        const inicio = parseHora(horario.inicio);
        const fim = parseHora(horario.fim);
        
        if (inicio !== null && fim !== null && inicio < fim) {
          horarioNormalizado[diaEnum] = { inicio, fim };
        } else {
          console.warn(`Horário inválido para ${diaStr}:`, horario);
          horarioNormalizado[diaEnum] = null;
        }
      } else {
        console.warn(`Formato de horário inválido para ${diaStr}:`, horario);
        horarioNormalizado[diaEnum] = null;
      }
    }
    
    return horarioNormalizado;
  }
  
  // Se é string, tenta converter da estrutura antiga (compatibilidade)
  if (typeof horarioFuncionamento === 'string') {
    return converterStringParaObjeto(horarioFuncionamento);
  }
  
  return null;
}

/**
 * Converte string antiga para nova estrutura (compatibilidade)
 */
function converterStringParaObjeto(horarioStr) {
  if (!horarioStr) return null;
  
  const horarioNormalizado = {};
  
  // Inicializa todos os dias como fechados
  Object.values(DIAS_SEMANA).forEach(dia => {
    horarioNormalizado[dia] = null;
  });
  
  // Divide por "|" para separar diferentes períodos
  const periodos = horarioStr.split('|').map(p => p.trim()).filter(p => p.length > 0);
  
  periodos.forEach(periodo => {
    // Verifica se está explicitamente fechado
    if (periodo.toLowerCase().includes('fechado')) {
      const match = periodo.match(/([^:]+):/i);
      if (match) {
        const diasStr = match[1].trim();
        const dias = parseDiasString(diasStr);
        dias.forEach(dia => {
          horarioNormalizado[dia] = null;
        });
      }
      return;
    }
    
    // Extrai dias e horário
    const match = periodo.match(/([^:]+):\s*(\d{1,2}h(?:\d{2})?)\s*às\s*(\d{1,2}h(?:\d{2})?)/i);
    
    if (match) {
      const diasStr = match[1].trim();
      const horaInicioStr = match[2].trim();
      const horaFimStr = match[3].trim();
      
      const horaInicio = parseHora(horaInicioStr);
      const horaFim = parseHora(horaFimStr);
      const dias = parseDiasString(diasStr);
      
      if (horaInicio !== null && horaFim !== null && horaInicio < horaFim && dias.length > 0) {
        dias.forEach(dia => {
          horarioNormalizado[dia] = { inicio: horaInicio, fim: horaFim };
        });
      }
    }
  });
  
  return horarioNormalizado;
}

/**
 * Parse de string de dias para array de enums
 */
function parseDiasString(diasStr) {
  if (!diasStr) return [];
  
  const diasLower = diasStr.toLowerCase().trim();
  const dias = [];
  
  // Caso especial: "Segunda a Domingo" = todos os dias
  if (diasLower.includes('segunda') && diasLower.includes('domingo') && diasLower.includes(' a ')) {
    return Object.values(DIAS_SEMANA);
  }
  
  // Verifica se tem range E lista (ex: "Segunda a Quinta e Sexta")
  if (diasLower.includes(' a ') && (diasLower.includes(' e ') || diasLower.includes(','))) {
    const todosDias = [];
    
    // Primeiro, identifica o range
    const rangeMatch = diasLower.match(/([^, e]+ a [^, e]+)/);
    if (rangeMatch) {
      const rangeStr = rangeMatch[1].trim();
      const [inicio, fim] = rangeStr.split(' a ').map(d => d.trim());
      const inicioNum = stringParaDia(inicio);
      const fimNum = stringParaDia(fim);
      
      if (inicioNum !== undefined && fimNum !== undefined) {
        if (inicioNum <= fimNum) {
          for (let i = inicioNum; i <= fimNum; i++) {
            todosDias.push(i);
          }
        } else {
          // Range que cruza o fim de semana
          for (let i = inicioNum; i <= 6; i++) {
            todosDias.push(i);
          }
          for (let i = 0; i <= fimNum; i++) {
            todosDias.push(i);
          }
        }
      }
    }
    
    // Depois, identifica os dias adicionais
    const separador = diasLower.includes(' e ') ? ' e ' : ',';
    const partes = diasLower.split(separador);
    
    partes.forEach(parte => {
      parte = parte.trim();
      if (!parte.includes(' a ')) {
        const diaNum = stringParaDia(parte);
        if (diaNum !== undefined) {
          todosDias.push(diaNum);
        }
      }
    });
    
    return [...new Set(todosDias)];
  }
  
  // Verifica se é um range simples
  if (diasLower.includes(' a ')) {
    const partes = diasLower.split(' a ').map(p => p.trim());
    if (partes.length === 2) {
      const inicio = stringParaDia(partes[0]);
      const fim = stringParaDia(partes[1]);
      
      if (inicio !== undefined && fim !== undefined) {
        if (inicio <= fim) {
          for (let i = inicio; i <= fim; i++) {
            dias.push(i);
          }
        } else {
          // Range que cruza o fim de semana
          for (let i = inicio; i <= 6; i++) {
            dias.push(i);
          }
          for (let i = 0; i <= fim; i++) {
            dias.push(i);
          }
        }
      }
    }
    return dias;
  }
  
  // Verifica se são múltiplos dias separados por "e" ou ","
  if (diasLower.includes(' e ') || (diasLower.includes(',') && !diasLower.includes(' a '))) {
    const separador = diasLower.includes(' e ') ? ' e ' : ',';
    const diasLista = diasLower.split(separador).map(d => d.trim());
    
    diasLista.forEach(diaStr => {
      const diaNum = stringParaDia(diaStr);
      if (diaNum !== undefined) {
        dias.push(diaNum);
      }
    });
    
    return dias;
  }
  
  // Dia único
  const diaNum = stringParaDia(diasLower);
  if (diaNum !== undefined) {
    return [diaNum];
  }
  
  return [];
}

/**
 * Verifica se a loja está aberta no momento atual
 * 
 * @param {object|string} horarioFuncionamento - Estrutura de horário (objeto ou string para compatibilidade)
 * @returns {boolean} - true se aberto, false se fechado
 */
export function estaAberto(horarioFuncionamento) {
  if (!horarioFuncionamento) return false;
  
  const agora = new Date();
  const diaAtual = agora.getDay(); // 0 = Domingo, 1 = Segunda, ..., 6 = Sábado
  const horaAtual = agora.getHours() * 60 + agora.getMinutes(); // Minutos desde meia-noite
  
  const horarioNormalizado = normalizarHorarioFuncionamento(horarioFuncionamento);
  
  if (!horarioNormalizado) return false;
  
  // Verifica o horário do dia atual
  const horarioDia = horarioNormalizado[diaAtual];
  
  // Se é null, está fechado
  if (horarioDia === null || horarioDia === undefined) {
    return false;
  }
  
  // Verifica se a hora atual está dentro do horário
  const horaFimAjustada = horarioDia.fim % 60 === 0 
    ? horarioDia.fim + 59  // Hora cheia: adiciona 59 minutos (ex: 20h -> 20h59)
    : horarioDia.fim;      // Com minutos: usa o horário exato (ex: 20h30 -> 20h30)
  
  return horaAtual >= horarioDia.inicio && horaAtual <= horaFimAjustada;
}

/**
 * Retorna o status da loja (aberto/fechado) baseado no horário
 */
export function getStatusLoja(horarioFuncionamento) {
  return estaAberto(horarioFuncionamento) ? 'open' : 'closed';
}

/**
 * Retorna texto do status em português
 */
export function getStatusTexto(horarioFuncionamento) {
  return estaAberto(horarioFuncionamento) ? 'Aberto' : 'Fechado';
}

/**
 * Formata o horário de funcionamento para exibição
 * 
 * @param {object|string} horarioFuncionamento - Estrutura de horário
 * @returns {string} - String formatada para exibição
 */
export function formatarHorarioFuncionamento(horarioFuncionamento) {
  if (!horarioFuncionamento) return 'Horário não informado';
  
  const horarioNormalizado = normalizarHorarioFuncionamento(horarioFuncionamento);
  
  if (!horarioNormalizado) {
    // Se não conseguiu normalizar, retorna a string original
    return typeof horarioFuncionamento === 'string' ? horarioFuncionamento : 'Horário inválido';
  }
  
  // Agrupa dias com mesmo horário
  const grupos = {};
  
  Object.entries(horarioNormalizado).forEach(([dia, horario]) => {
    if (horario === null) {
      const diaNome = NOMES_DIAS[dia];
      if (!grupos['fechado']) grupos['fechado'] = [];
      grupos['fechado'].push(diaNome);
    } else {
      const chave = `${horario.inicio}-${horario.fim}`;
      if (!grupos[chave]) grupos[chave] = { horario, dias: [] };
      grupos[chave].dias.push(NOMES_DIAS[dia]);
    }
  });
  
  const partes = [];
  
  Object.entries(grupos).forEach(([chave, dados]) => {
    if (chave === 'fechado') {
      if (dados.length === 1) {
        partes.push(`${dados[0]}: Fechado`);
      } else if (dados.length > 1) {
        partes.push(`${dados.join(', ')}: Fechado`);
      }
    } else {
      const { horario, dias } = dados;
      const inicioStr = formatarHora(horario.inicio);
      const fimStr = formatarHora(horario.fim);
      
      if (dias.length === 1) {
        partes.push(`${dias[0]}: ${inicioStr} às ${fimStr}`);
      } else if (dias.length === 2) {
        partes.push(`${dias[0]} e ${dias[1]}: ${inicioStr} às ${fimStr}`);
      } else {
        const primeiro = dias[0];
        const ultimo = dias[dias.length - 1];
        partes.push(`${primeiro} a ${ultimo}: ${inicioStr} às ${fimStr}`);
      }
    }
  });
  
  return partes.join(' | ');
}

/**
 * Formata minutos desde meia-noite para string "14h30"
 */
function formatarHora(minutos) {
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;
  return mins > 0 ? `${horas}h${mins.toString().padStart(2, '0')}` : `${horas}h`;
}
